import { useToast } from "@chakra-ui/react";
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { AdminCompany, Company } from "models/company";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";
import { apiCurrentCompanyAdminPath, apiCurrentCompanyPath } from "helpers/url/pages/v1";
import { CompanyUpdateFormValues } from "./DynamicForm/formDefinition";

export const companyKeys = {
  all: ["companies"] as const,
  details: () => [...companyKeys.all, "detail"] as const,
  detail: (id: string) => [...companyKeys.details(), id] as const,
  current: () => [...companyKeys.all, "current"] as const,
  currentAdmin: () => [...companyKeys.current(), "admin"] as const,
  admin: () => [...companyKeys.all, "admin"] as const,
  adminDetail: (id: string) => [...companyKeys.admin(), id] as const,
};

export function setNewCompanyInQueryClient(queryClient: QueryClient, company: Company) {
  queryClient.setQueryData(companyKeys.current(), (oldData: Company | undefined) => {
    if (!oldData) {
      return company;
    }
    return {
      ...oldData,
      ...company,
    };
  });
}

export const currentCompanyQueryArgs = () => ({
  queryKey: companyKeys.current(),
  queryFn: async () => apiClient.get<Company>(apiCurrentCompanyPath()),
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 3, // 3 minutes
});

export const useCurrentCompanyQuery = (options = {}) => useQuery<Company>({
  ...currentCompanyQueryArgs(),
  ...options,
});

export const useCurrentCompanyAdminQuery = (options = {}) => useQuery<AdminCompany>({
  queryKey: companyKeys.currentAdmin(),
  queryFn: async () => apiClient.get<AdminCompany>(apiCurrentCompanyAdminPath()),
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 3,
  ...options,
});

export const useAdminCompanyQuery = (companyId: string) => useQuery<Company>({
  queryKey: companyKeys.adminDetail(companyId),
  queryFn: async () => apiClient.get(generatePath(API_ROUTES.admin.companies.show, { id: companyId })),
});

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (params: { data: CompanyUpdateFormValues; dirtyFields: Record<string, boolean> }) => {
      const { data, dirtyFields } = params;
      const updateData = Object.fromEntries(
        Object.entries(data).filter(([key]) => dirtyFields[key as keyof CompanyUpdateFormValues] === true),
      );
      return apiClient.put<Company>(apiCurrentCompanyPath(), {
        company: updateData,
      });
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        status: "success",
      });
      return queryClient.invalidateQueries({ queryKey: companyKeys.current() });
    },
    onError: (mutateError) => {
      console.log("userCreateError", mutateError);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });
}
